import React, { useState } from "react";
import { Button, Form, InputGroup, Table } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomModal from "../../../components/customModal/CustomModal";
import CreateBarcodeForm, { CreateBarcodeFormValues, CreateBarcodeFormPrefillData } from "./CreateBarcodeForm";
import moment from "moment";
import { handleQuantityInput } from "../../../utils/commonUtils";
import { useAppSelector } from "../../../redux/hooks";
import { selectSystemSettings, SettingName } from "../../../redux/settings/settingsSlice";

export interface BarcodeItem {
  id: number;
  barcode: string;
  ndc: string;
  lot: string;
  expDate: string;
  source: string;
  orderingProductNo: string;
}

interface ExistingBarcodesModalProps {
  barcodeItems: BarcodeItem[];
  onSave: (selectedBarcode: BarcodeItem, qty: string, refInfo: string) => void;
  onClose: () => void;
  onNewBarcodeSave: (values: CreateBarcodeFormValues) => void;
  prefillData?: CreateBarcodeFormPrefillData;
}

const existingBarcodeValidationSchema = Yup.object({
  qty: Yup.number()
    .typeError("Qty must be a number")
    .min(0, "Qty must be a positive number")
    .required("Qty is required"),
  refInfo: Yup.string(),
});

const ExistingBarcodesModal: React.FC<ExistingBarcodesModalProps> = ({
  barcodeItems,
  onSave,
  onClose,
  onNewBarcodeSave,
  prefillData,
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState<BarcodeItem | null>(null);
  const unit = prefillData?.inventoryUnitName || "";

  const systemSettings = useAppSelector(selectSystemSettings);
  const itemPageSettings = systemSettings[SettingName.ITEM_PAGE_SETTINGS] ?? {};
  const requiredDecimalPoint = itemPageSettings?.RequiredDecimalPoint ?? 2;

  const formik = useFormik({
    initialValues: {
      qty: prefillData?.qty || "",
      refInfo: prefillData?.refInfo || "",
    },
    validationSchema: existingBarcodeValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (selectedBarcode) {
        onSave(selectedBarcode, values.qty, values.refInfo);
      }
    },
  });

  const handleQtyInput = (e: React.FormEvent<HTMLInputElement>) => {
    handleQuantityInput(e, requiredDecimalPoint);
  };

  const handleRowClick = (barcode: BarcodeItem) => {
    setSelectedBarcode(barcode);
    formik.resetForm({
      values: {
        qty: prefillData?.qty || "",
        refInfo: prefillData?.refInfo || "",
      },
    });
  };

  return (
    <CustomModal
      id="existing-barcodes-modal"
      title="Existing Barcodes"
      overlayClass="existing-barcodes-overlay"
      onClose={onClose}
      content={
        <div className="existing-barcodes-content">
          <Table bordered hover size="sm">
            <thead>
              <tr>
                {/* <th>Id</th> */}
                <th>Barcode</th>
                <th>NDC</th>
                <th>Lot</th>
                <th>Exp Date</th>
                <th>Source</th>
                <th className="text-center">Ordering Product No</th>
              </tr>
            </thead>
            <tbody>
              {barcodeItems.length > 0 ? (
                barcodeItems.map((barcode) => (
                  <tr
                    key={barcode.id}
                    className={selectedBarcode?.id === barcode.id ? "selected-row" : ""}
                    onClick={() => handleRowClick(barcode)}
                    style={{ cursor: "pointer" }}
                  >
                    {/* <td>{barcode.id}</td> */}
                    <td>{barcode.barcode}</td>
                    <td>{barcode.ndc}</td>
                    <td>{barcode.lot}</td>
                    <td>{barcode.expDate ? moment.parseZone(barcode.expDate).format("M/D/YYYY") : ""}</td>
                    <td>{barcode.source}</td>
                    <td className="text-center">{barcode.orderingProductNo}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center"
                    style={{ color: "red", fontWeight: "bold" }}
                  >
                    No matching barcodes found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <p className="record-count">
            {barcodeItems.length} record(s)
          </p>

          {selectedBarcode && !showCreateForm && (
            <div className="barcode-form-grid">
              <div className="barcode-form-row">
                <div className="barcode-form-field">
                  <label>Qty: <span className="text-danger">*</span></label>
                  <InputGroup>
                    <Form.Control
                      size="sm"
                      type="text"
                      inputMode="decimal"
                      name="qty"
                      value={formik.values.qty}
                      onChange={formik.handleChange}
                      onInput={handleQtyInput}
                      onBlur={formik.handleBlur}
                      placeholder="Enter Quantity"
                      isInvalid={formik.touched.qty && !!formik.errors.qty}
                    />
                    ({unit})
                  </InputGroup>
                  {formik.touched.qty && formik.errors.qty && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {formik.errors.qty}
                    </Form.Control.Feedback>
                  )}
                </div>
                <div className="barcode-form-field">
                  <label>Ref Info:</label>
                  <Form.Control
                    size="sm"
                    name="refInfo"
                    value={formik.values.refInfo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
            </div>
          )}

          {showCreateForm ? (
            <CreateBarcodeForm
              onSave={(values) => {
                onNewBarcodeSave(values);
                onClose();
              }}
              onCancel={() => setShowCreateForm(false)}
              prefillData={prefillData}
            />
          ) : (
            <div className="modal--action--btn--wrapper">
              <Button
                variant="outline-primary"
                onClick={() => setShowCreateForm(true)}
              >
                Create New Barcode
              </Button>
              <Button
                variant="primary"
                onClick={() => formik.handleSubmit()}
                disabled={!selectedBarcode}
              >
                Save
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      }
    />
  );
};

export default ExistingBarcodesModal;
